import { RULE_ACTIONS, type ThreadAction } from '@/shared-responder-v2-core';

export interface RuleActionsPayload {
  readonly preActivationActionType?: string | null;
  readonly preActivationActionData?: string | null;
  readonly postActivationActionType?: string | null;
  readonly postActivationActionData?: string | null;
}

/**
 * One `RuleAction` narrowed onto the port's closed `ThreadAction` union.
 * `Redirect`/`CustomRedirect`/`ReviewRedirect` all carry their URL in
 * `data`, mirroring `getRedirectUrl` in
 * `libs/shared-project-response/src/lib/project-response.helpers.ts`; a
 * redirect with no URL data is dropped rather than guessed at. The five
 * panel-routing types (`Panel*Redirect`) need panel-provider context
 * (`spid`/`rid`/`rdud`) this port never carries, so they collapse onto the
 * generic `screenOut` signal instead of a URL. `FeedbackMessage`, `Question`
 * and `SuccessMessage` are not termination actions at all and are dropped.
 * Anything the two schemas' `RuleActionType` enums don't declare is dropped
 * too, since the type is now a plain `string` rather than a closed enum.
 */
function toThreadAction(
  type: string | null | undefined,
  data: string | null | undefined
): ThreadAction | null {
  if (type === null || type === undefined) {
    return null;
  }

  if (type === RULE_ACTIONS.CustomEndConversation) {
    return { type: 'endConversation', message: data ?? null };
  }

  if (
    type === RULE_ACTIONS.Redirect ||
    type === RULE_ACTIONS.CustomRedirect ||
    type === RULE_ACTIONS.ReviewRedirect
  ) {
    return data ? { type: 'redirect', url: data } : null;
  }

  if (
    type === RULE_ACTIONS.PanelCompleteRedirect ||
    type === RULE_ACTIONS.PanelOverQuotaRedirect ||
    type === RULE_ACTIONS.PanelQualityControlRedirect ||
    type === RULE_ACTIONS.PanelSecurityRedirect ||
    type === RULE_ACTIONS.PanelTerminateRedirect
  ) {
    return { type: 'screenOut' };
  }

  return null;
}

/**
 * Narrows the backend's pre/post-activation pair onto the port's
 * `ThreadAction[]`, preserving pre-activation before post-activation order.
 * An unrecognised action type is dropped, never guessed at — `actions` only
 * ever contains what the responder can actually render.
 */
export function toThreadActions(
  actions: RuleActionsPayload | null | undefined
): readonly ThreadAction[] {
  if (!actions) {
    return [];
  }

  const resolved: ThreadAction[] = [];

  const pre = toThreadAction(
    actions.preActivationActionType,
    actions.preActivationActionData
  );
  if (pre !== null) {
    resolved.push(pre);
  }

  const post = toThreadAction(
    actions.postActivationActionType,
    actions.postActivationActionData
  );
  if (post !== null) {
    resolved.push(post);
  }

  return resolved;
}
